import React, { useMemo, useCallback, useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import AsyncSelect from 'react-select/async';
import { SingleValue, components, OptionProps, GroupBase, SelectInstance } from 'react-select';
import { useDarkMode } from '../../hooks/useDarkMode';
import { getSelectStyles } from './selectStyles';

interface AsyncOption<T> {
  value: string;
  label: string;
  data: T;
  secondaryLabel?: string;
}

export interface AsyncSearchSelectHandle {
  focus: () => void;
}

interface AsyncSearchSelectProps<T> {
  /** Function to load options based on search input */
  loadOptions: (inputValue: string) => Promise<T[]>;
  /** Function to get the unique ID/value from an item */
  getValue: (item: T) => string;
  /** Function to get the display label from an item */
  getLabel: (item: T) => string;
  /** Optional function to get a secondary label for rich display */
  getSecondaryLabel?: (item: T) => string;
  /** Called when an item is selected */
  onSelect: (item: T) => void;
  /** Called when the input value changes (for external search handling) */
  onInputChange?: (value: string) => void;
  /** Called when the select is focused */
  onFocus?: () => void;
  /** Current value (the ID string) */
  value?: string;
  /** Initial display value when value is set but options haven't loaded */
  initialDisplayValue?: string;
  /** Label to show above the select */
  label?: React.ReactNode;
  /** Placeholder text */
  placeholder?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Message to show when no options are found */
  noOptionsMessage?: string;
  /** Whether to show loading state */
  isLoading?: boolean;
  /** Debounce delay in ms for search (default: 300) */
  debounceMs?: number;
  /** Style variant */
  variant?: 'default' | 'compact';
  /** Additional class name */
  className?: string;
  /** Class name for the input */
  inputClassName?: string;
}

// Custom Option component with secondary label support
const CustomOption = <T,>(props: OptionProps<AsyncOption<T>, false, GroupBase<AsyncOption<T>>>) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div>
        <div className="font-medium">{data.label}</div>
        {data.secondaryLabel && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {data.secondaryLabel}
          </div>
        )}
      </div>
    </components.Option>
  );
};

/**
 * An async searchable select component that loads options from a backend.
 * Supports rich display with primary and secondary labels.
 *
 * @example
 * <AsyncSearchSelect
 *   loadOptions={async (query) => portService.getAll({ query, limit: 20 })}
 *   getValue={(port) => port.id}
 *   getLabel={(port) => `${port.port} (${port.code})`}
 *   getSecondaryLabel={(port) => `${port.code} - ${port.country}`}
 *   onSelect={(port) => setSelectedPortId(port.id)}
 *   value={selectedPortId}
 *   label="Port of Discharge"
 *   placeholder="Search port..."
 * />
 */
function AsyncSearchSelectInner<T>(
  {
    loadOptions,
    getValue,
    getLabel,
    getSecondaryLabel,
    onSelect,
    onInputChange,
    onFocus,
    value,
    initialDisplayValue = '',
    label,
    placeholder = 'Search...',
    disabled = false,
    noOptionsMessage = 'No options found',
    isLoading = false,
    debounceMs = 300,
    variant = 'compact',
    className = '',
    inputClassName = '',
  }: AsyncSearchSelectProps<T>,
  ref: React.Ref<AsyncSearchSelectHandle>
) {
  const { isDarkMode } = useDarkMode();
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<AsyncOption<T> | null>(null);
  const selectRef = useRef<SelectInstance<AsyncOption<T>, false>>(null);

  useImperativeHandle(ref, () => ({
    focus: () => selectRef.current?.focus(),
  }));

  const customStyles = useMemo(
    () => getSelectStyles<AsyncOption<T>>(isDarkMode, variant),
    [isDarkMode, variant]
  );

  // Convert items to options
  const itemsToOptions = useCallback(
    (items: T[]): AsyncOption<T>[] =>
      items.map((item) => ({
        value: getValue(item),
        label: getLabel(item),
        data: item,
        secondaryLabel: getSecondaryLabel?.(item),
      })),
    [getValue, getLabel, getSecondaryLabel]
  );

  // Debounced load options
  const loadOptionsDebounced = useCallback(
    (inputValue: string): Promise<AsyncOption<T>[]> => {
      return new Promise((resolve) => {
        setTimeout(async () => {
          try {
            const items = await loadOptions(inputValue);
            resolve(itemsToOptions(items));
          } catch (error) {
            resolve([]);
          }
        }, debounceMs);
      });
    },
    [loadOptions, itemsToOptions, debounceMs]
  );

  // Handle selection
  const handleChange = (newValue: SingleValue<AsyncOption<T>>) => {
    setSelectedOption(newValue);
    if (newValue) {
      onSelect(newValue.data);
    }
  };

  // Handle input change - always keep inputValue in sync with react-select
  const handleInputChange = (newValue: string, actionMeta: { action: string }) => {
    // Always update inputValue to keep it in sync with react-select's internal state
    // This ensures input is cleared on selection (select-option), clear (clear), etc.
    setInputValue(newValue);
    // Only notify parent callback for actual user typing
    if (actionMeta.action === 'input-change') {
      onInputChange?.(newValue);
    }
  };

  // Handle focus
  const handleFocus = () => {
    onFocus?.();
  };

  // Clear local selection when parent clears the value (e.g., form reset)
  useEffect(() => {
    if (!value && selectedOption) {
      setSelectedOption(null);
    }
  }, [value, selectedOption]);

  // Create display value - trust local selection first, then fall back to parent props
  const displayValue = useMemo(() => {
    // Trust local selection if we have one (handles period between selection and parent prop update)
    if (selectedOption) {
      return selectedOption;
    }
    // Fall back to parent's value + cached display label (for remounts)
    if (value && initialDisplayValue) {
      return {
        value,
        label: initialDisplayValue,
        data: {} as T,
      };
    }
    return null;
  }, [selectedOption, value, initialDisplayValue]);

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <AsyncSelect<AsyncOption<T>, false>
        ref={selectRef}
        value={displayValue}
        onChange={handleChange}
        loadOptions={loadOptionsDebounced}
        onInputChange={handleInputChange}
        onFocus={handleFocus}
        inputValue={inputValue}
        placeholder={placeholder}
        isDisabled={disabled}
        isLoading={isLoading}
        isClearable
        isSearchable
        defaultOptions
        cacheOptions
        noOptionsMessage={() => noOptionsMessage}
        loadingMessage={() => 'Loading...'}
        styles={customStyles}
        components={{ Option: CustomOption }}
        className={`react-select-container ${inputClassName}`}
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        menuPosition="fixed"
        menuPlacement="auto"
      />
    </div>
  );
}

export const AsyncSearchSelect = forwardRef(AsyncSearchSelectInner) as <T>(
  props: AsyncSearchSelectProps<T> & { ref?: React.Ref<AsyncSearchSelectHandle> }
) => React.ReactElement;

export default AsyncSearchSelect;
